// components/daimon/DaimonPresence.tsx
'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Globe, X } from '@/components/ui/lucide-memo';
import { AppTooltip } from '@/components/ui/tooltip';
import { ListPermissionPrompt } from '@/components/list/ListPermissionPrompt';
import { DaimonOrb } from './DaimonOrb';
import { useDaimonFace } from './DaimonFace';
import type { Daimon } from './DaimonVoice';

/** Command twice on a Mac, Control twice elsewhere (no Command key there). */
export const SUMMON_IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const SHARE_SHORTCUT = SUMMON_IS_MAC ? '⌘⇧S' : 'Ctrl+Shift+S';
const SUMMON_KEY = SUMMON_IS_MAC ? 'Meta' : 'Control';

/** Her cursor: where it is, whether it shows, whether it is pressing, how long its glide lasts. */
export type DaimonPointer = { x: number; y: number; shown: boolean; pressing: boolean; ms: number };

/** ChatGPT's agent cursor: a white arrow, a thin dark edge, a soft glow. Its tip is the point. */
const Cursor = ({ pointer }: { pointer: DaimonPointer }) => (
  <div
    aria-hidden
    className="pointer-events-none fixed left-0 top-0 z-[2147483001]"
    style={{
      transform: `translate(${pointer.x}px, ${pointer.y}px)`,
      opacity: pointer.shown ? 1 : 0,
      transition: `transform ${pointer.ms}ms cubic-bezier(0.33, 0, 0.2, 1), opacity 220ms ease-out`,
    }}
  >
    {pointer.pressing ? <span className="absolute -left-4 -top-4 h-8 w-8 rounded-full border-2 border-white/70 animate-out fade-out-0 zoom-out-[1.8] duration-500 fill-mode-forwards" /> : null}
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      className="block"
      style={{
        margin: '-3.4px 0 0 -5.9px',
        transform: `scale(${pointer.pressing ? 0.86 : pointer.shown ? 1 : 0.6})`,
        transformOrigin: '5.9px 3.4px',
        transition: 'transform 140ms ease-out',
        filter: 'drop-shadow(0 0 7px rgba(140, 175, 255, 0.55)) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6))',
      }}
    >
      <path d="M5.5 3.2v17.1l4.4-4.2 3 6.5 3-1.3-3-6.4h6.1z" fill="#FFFFFF" stroke="#111111" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  </div>
);

// A SOLID RING THAT BREATHES (owner, 2026-09-29, twice: 'why is the ring broken', 'FIX THE RING').
// Any turning arc, even one over a faint full circle, reads as a broken ring once the faint part
// vanishes against the page. So the whole ring is always drawn at full strength, and 'working' is
// told by a slow glow pulse instead of motion around it.
/** A whole ring around her orb while she browses the web, so the user knows to wait. */
export type DaimonActivity = 'listening' | 'thinking' | 'speaking' | 'waiting' | null;
export const ACTIVITY_LABEL: Record<NonNullable<DaimonActivity>, string> = { listening: 'Listening', thinking: 'Thinking', speaking: 'Speaking', waiting: 'Your turn' };

/** The bubble's quiet sign of what the call is doing; browsing keeps its own ring. */
export const ActivityRing = ({ activity, browsing = false }: { activity: DaimonActivity; browsing?: boolean }) => {
  if (activity === 'listening') return <span aria-hidden className="pointer-events-none absolute -inset-[5px] rounded-full border-2 border-white/70 daimon-listen" />;
  // A whole ring, never a turning arc: any partial arc reads as a broken, off-centre circle
  // (owner, 2026-09-29 and 2026-10-10). Thinking is told by a soft blue pulse instead.
  if (activity === 'thinking' && !browsing) return <span aria-hidden className="pointer-events-none absolute -inset-[5px] rounded-full border-2 border-[#8FB0FF]/70 daimon-think" />;
  return null;
};
/** Waiting for the user dims the orb a little; every other state is at full strength. */
export const activityOpacity = (activity: DaimonActivity) => ({ opacity: activity === 'waiting' ? 0.78 : 1, transition: 'opacity 160ms ease-out' });

/** Every ring goes inside DaimonOrb (its children), whose box is the ball itself. */
export const BrowsingRing = () => (
  <span aria-hidden className="pointer-events-none absolute -inset-[5px] rounded-full border-2 border-[#8FB0FF] daimon-ring-breathe" />
);

// HER ORB CAN BE MOVED (owner, 2026-09-29: 'we should be able to drag and drop the Daimon bubble
// and it should stick around the edges of the screen'). Dragged anywhere, it glides on release to
// the nearer side edge at the height it was dropped, as a phone's floating bubble does; the spot is
// this browser's, so it lives in localStorage rather than the user's preferences.
const ORB = 64;
const EDGE = 20;
const DOCK_KEY = 'daimon-orb-dock';
type Dock = { side: 'left' | 'right'; top: number };
const readDock = (): Dock | null => {
  if (typeof window === 'undefined') return null;
  try {
    const saved = JSON.parse(window.localStorage.getItem(DOCK_KEY) ?? 'null') as Dock | null;
    return saved && (saved.side === 'left' || saved.side === 'right') && Number.isFinite(saved.top) ? saved : null;
  } catch { return null; }
};
const clampTop = (top: number) => Math.min(Math.max(EDGE, top), window.innerHeight - ORB - EDGE);

function useEdgeDock() {
  const [dock, setDock] = useState<Dock | null>(readDock);
  const [drag, setDrag] = useState<{ left: number; top: number } | null>(null);
  const [, setViewport] = useState(0);
  const start = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const moved = useRef(false);
  // A drag ends on the button it started on; its click must not also open her page.
  const swallowClick = useRef(false);
  useEffect(() => {
    const onResize = () => setViewport((n) => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const resting = dock ?? { side: 'right' as const, top: typeof window === 'undefined' ? 0 : window.innerHeight - ORB - EDGE };
  const position = drag ?? {
    left: resting.side === 'left' ? EDGE : (typeof window === 'undefined' ? 0 : window.innerWidth - ORB - EDGE),
    top: typeof window === 'undefined' ? 0 : clampTop(resting.top),
  };
  const handlers = {
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0 || (event.target as HTMLElement).closest('[data-daimon-end]')) return;
      start.current = { x: event.clientX, y: event.clientY, left: position.left, top: position.top };
      moved.current = false;
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
      const from = start.current;
      if (!from) return;
      const dx = event.clientX - from.x;
      const dy = event.clientY - from.y;
      if (!moved.current && Math.hypot(dx, dy) < 4) return;
      moved.current = true;
      setDrag({
        left: Math.min(Math.max(0, from.left + dx), window.innerWidth - ORB),
        top: Math.min(Math.max(0, from.top + dy), window.innerHeight - ORB),
      });
    },
    onPointerUp: () => {
      start.current = null;
      if (!moved.current || !drag) return;
      const next: Dock = { side: drag.left + ORB / 2 < window.innerWidth / 2 ? 'left' : 'right', top: clampTop(drag.top) };
      setDock(next);
      setDrag(null);
      swallowClick.current = true;
      try { window.localStorage.setItem(DOCK_KEY, JSON.stringify(next)); } catch { /* private mode: it stays for this visit */ }
    },
  };
  const consumeDragClick = () => { const was = swallowClick.current; swallowClick.current = false; return was; };
  return { position, side: resting.side, dragging: drag !== null, handlers, consumeDragClick };
}

const SCREEN_ASK = { id: 'daimon-screen', title: 'Let the Daimon see your screen?', label: 'Shares', items: [{ id: 'tab', name: 'This tab, until the call ends' }] };

/**
 * What the user sees while the Daimon uses Fater for them: a thin blue frame around the screen, her
 * cursor, and a pill to take over (Escape or any click of their own does the same). Away from her
 * page while her call is on, her orb alone sits in the corner, one tap from her page.
 */
export const DaimonPresence = ({ daimon }: { daimon: Daimon }) => {
  const { driving, takeOver } = daimon;
  useEffect(() => {
    if (!driving) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') takeOver(); };
    // Her own presses are synthetic; a real one means the user wants the controls back.
    const onPointer = (event: PointerEvent) => { if (event.isTrusted) takeOver(); };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onPointer, true);
    };
  }, [driving, takeOver]);

  // Cmd+Shift+S (Ctrl+Shift+S elsewhere) shares the screen or stops sharing it (owner, 2026-10-02).
  // A key press is the user gesture the browser's share dialog requires.
  const { toggleScreenShare } = daimon;
  useEffect(() => {
    const onShare = (event: KeyboardEvent) => {
      if (event.repeat || !event.shiftKey || event.key.toLowerCase() !== 's' || !(SUMMON_IS_MAC ? event.metaKey : event.ctrlKey)) return;
      event.preventDefault();
      void toggleScreenShare();
    };
    window.addEventListener('keydown', onShare, true);
    return () => window.removeEventListener('keydown', onShare, true);
  }, [toggleScreenShare]);

  // Command tapped twice (Control where there is no Command), nothing else between. Not Option
  // twice, Claude desktop's quick entry, nor Option+Space, ChatGPT's (owner, 2026-09-29).
  const { summon } = daimon;
  // A tap is Command pressed and released quickly with the page focused throughout (owner,
  // 2026-10-05: 'the daimon activates by itself'): macOS keeps the other key of its own shortcuts
  // (Cmd+Tab, Cmd+Space, a screenshot), so the page saw a bare Command and two of them read as a summon.
  useEffect(() => {
    let lastTap = 0;
    let pressedAt = 0;
    const onDown = (event: KeyboardEvent) => {
      pressedAt = event.key === SUMMON_KEY && !event.repeat ? performance.now() : 0;
      if (!pressedAt) lastTap = 0;
    };
    const onUp = (event: KeyboardEvent) => {
      if (event.key !== SUMMON_KEY || !pressedAt) return;
      const now = performance.now();
      const quick = now - pressedAt < 300;
      pressedAt = 0;
      if (!quick || !document.hasFocus()) { lastTap = 0; return; }
      if (now - lastTap < 400) { lastTap = 0; summon(); } else lastTap = now;
    };
    const reset = () => { pressedAt = 0; lastTap = 0; };
    window.addEventListener('keydown', onDown, true);
    window.addEventListener('keyup', onUp, true);
    window.addEventListener('pointerdown', reset, true);
    window.addEventListener('blur', reset);
    document.addEventListener('visibilitychange', reset);
    return () => {
      window.removeEventListener('keydown', onDown, true);
      window.removeEventListener('keyup', onUp, true);
      window.removeEventListener('pointerdown', reset, true);
      window.removeEventListener('blur', reset);
      document.removeEventListener('visibilitychange', reset);
    };
  }, [summon]);

  const busy = daimon.state === 'connecting' || daimon.state === 'live';
  // Her face replaces the docked orb while it is on (components/daimon/DaimonFace.tsx).
  const faceOn = useDaimonFace((state) => state.on);
  const orbDock = useEdgeDock();

  return (
    <>
      {driving ? (
        <>
          <div
            aria-hidden
            className="pointer-events-none fixed inset-0 z-[2147483000] rounded-[10px] animate-in fade-in-0 duration-200"
            style={{ boxShadow: 'inset 0 0 0 2px rgba(56, 98, 194, 0.95), inset 0 0 28px rgba(56, 98, 194, 0.35)' }}
          />
          <div data-daimon-ui className="fixed left-1/2 top-3 z-[2147483002] flex -translate-x-1/2 items-center gap-2 rounded-full border border-[#2F2F2F] bg-[#1B1B1B] py-1 pl-2 pr-1 text-white shadow-[0_8px_24px_rgba(0,0,0,0.45)] animate-in fade-in-0 slide-in-from-top-1 duration-200">
            <DaimonOrb size={18} level={daimon.level} />
            <span style={{ fontSize: 13 }}>Daimon is using Fater</span>
            <button
              type="button"
              onClick={takeOver}
              className="flex items-center gap-1.5 rounded-full bg-white/[0.08] px-2.5 py-1 transition-colors hover:bg-white/[0.14]"
              style={{ fontSize: 13 }}
            >
              Take over
              <kbd className="rounded-[4px] bg-white/[0.08] px-1 text-[#AFAFAF]" style={{ fontSize: 11 }}>Esc</kbd>
            </button>
          </div>
        </>
      ) : null}
      {daimon.pointer ? <Cursor pointer={daimon.pointer} /> : null}
      {daimon.screenAsk && !driving && !daimon.approval ? (
        <div data-daimon-ui className="fixed bottom-24 right-5 z-[2147483002] w-[380px] max-w-[calc(100vw-40px)] shadow-[0_8px_24px_rgba(0,0,0,0.45)] animate-in fade-in-0 slide-in-from-bottom-1 duration-200">
          <ListPermissionPrompt
            request={SCREEN_ASK}
            onAnswer={(decision) => void daimon.answerScreenAsk(decision !== 'deny')}
          />
        </div>
      ) : null}
      {daimon.approval && !driving ? (
        <div data-daimon-ui className="fixed bottom-24 right-5 z-[2147483002] w-[380px] max-w-[calc(100vw-40px)] shadow-[0_8px_24px_rgba(0,0,0,0.45)] animate-in fade-in-0 slide-in-from-bottom-1 duration-200">
          <ListPermissionPrompt key={daimon.approval.id} request={daimon.approval} onAnswer={(decision) => void daimon.answerApproval(decision === 'deny' ? 'deny' : 'once')} />
        </div>
      ) : null}
      {busy && daimon.docked && !faceOn ? (
        <div
          data-daimon-ui
          className={`fixed z-[2147482999] touch-none select-none animate-in fade-in-0 zoom-in-[0.9] duration-300 ${orbDock.dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ left: orbDock.position.left, top: orbDock.position.top, transition: orbDock.dragging ? 'none' : 'left 260ms cubic-bezier(0.2, 0.8, 0.2, 1), top 260ms cubic-bezier(0.2, 0.8, 0.2, 1)' }}
          {...orbDock.handlers}
        >
          <div className="group relative">
            <AppTooltip content={daimon.activity ? `Back to the Daimon · ${ACTIVITY_LABEL[daimon.activity]}` : 'Back to the Daimon'}>
              <button type="button" onClick={() => { if (!orbDock.consumeDragClick()) daimon.returnToConversation(); }} aria-label={daimon.activity ? `Back to the Daimon, ${ACTIVITY_LABEL[daimon.activity].toLowerCase()}` : 'Back to the Daimon'} className="relative block rounded-full" data-daimon-orb data-activity={daimon.activity ?? undefined}>
                <span className="block" style={activityOpacity(daimon.activity)}>
                  <DaimonOrb size={64} level={daimon.level}>
                    <ActivityRing activity={daimon.activity} browsing={daimon.browsing !== null} />
                    {daimon.browsing !== null ? <BrowsingRing /> : null}
                  </DaimonOrb>
                </span>
              </button>
            </AppTooltip>
            {daimon.browsing !== null ? (
              <div role="status" className={`absolute top-1/2 flex -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-[#2F2F2F] bg-[#1B1B1B] px-3 py-1.5 text-[#ECECEC] shadow-[0_8px_24px_rgba(0,0,0,0.45)] animate-in fade-in-0 duration-200 ${orbDock.side === 'left' ? 'left-[76px] slide-in-from-left-1' : 'right-[76px] slide-in-from-right-1'}`}>
                <Globe size={14} className="shrink-0 text-[#8FB0FF]" />
                <span className="thinking-text-animated" style={{ fontSize: 13 }}>Browsing the web</span>
              </div>
            ) : null}
            <AppTooltip content="End">
              <button
                type="button"
                onClick={daimon.stop}
                data-daimon-end
                aria-label="End the conversation"
                className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border border-[#2F2F2F] bg-[#1B1B1B] text-[#E5E5E5] opacity-0 transition-opacity hover:bg-[#262626] group-hover:opacity-100 focus-visible:opacity-100"
              >
                <X size={13} strokeWidth={2.4} />
              </button>
            </AppTooltip>
          </div>
        </div>
      ) : null}
    </>
  );
};
